import { useCallback, useEffect, useState } from "react";
import { usePlaidLink } from "react-plaid-link";
import { Bar, BarChart, ResponsiveContainer, Tooltip, XAxis } from "recharts";
import { Check, ChevronLeft, ChevronRight, Copy, Download, Landmark, Loader2, RefreshCw, Sparkles, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { apiDelete, apiGet, apiPatch, apiPost } from "@/lib/apiAdmin";
import api from "@/lib/api";
import { HUD, fmtMoney } from "./theme";

const KIND_STYLE = { income: "text-emerald-300", expense: "text-rose-300", transfer: "text-sky-300", personal: "text-gray-400" };
const err = (e) => toast.error(e?.response?.data?.detail || "Something went wrong");

const ConnectButton = ({ onDone, onToken }) => {
  const [token, setToken] = useState(null);
  const onSuccess = useCallback((public_token, meta) => {
    apiPost("/admin/bank/exchange", { public_token, institution_name: meta?.institution?.name })
      .then(() => { toast.success("Bank connected, importing transactions"); onDone(); }).catch(err);
  }, [onDone]);
  const { open, ready } = usePlaidLink({ token, onSuccess });
  useEffect(() => { if (token && ready) open(); }, [token, ready, open]);
  return (
    <button className={`${HUD.btn} h-10 px-5 text-sm flex items-center gap-2`} data-testid="bank-connect-btn"
      onClick={() => apiPost("/admin/bank/link-token").then((d) => { localStorage.setItem("plaid_link_token", d.link_token); onToken?.(d); setToken(d.link_token); }).catch(err)}>
      <Landmark className="w-4 h-4" /> Connect bank account
    </button>
  );
};

const Kpi = ({ label, value, cls, testid }) => (
  <div className={`${HUD.panel} p-4`} data-testid={testid}>
    <p className={HUD.label}>{label}</p>
    <p className={`font-['Outfit'] text-2xl font-semibold mt-1 ${cls}`}>{value}</p>
  </div>
);

const ReviewRow = ({ t, cats, onDone }) => {
  const sug = t.ai_suggestion || {};
  const [kind, setKind] = useState(sug.kind || t.kind || "expense");
  const [cat, setCat] = useState(sug.category || "");
  const [remember, setRemember] = useState(true);
  const [busy, setBusy] = useState(false);
  const save = () => {
    setBusy(true);
    apiPatch(`/admin/bank/transactions/${t.transaction_id}`, { kind, category: cat || null, remember })
      .then((r) => { toast.success(`Filed as ${r.kind} / ${r.category}${r.also_applied ? ` (+${r.also_applied} similar)` : ""}`); onDone(); })
      .catch(err).finally(() => setBusy(false));
  };
  return (
    <div className="p-3 flex flex-wrap items-center gap-3" data-testid={`bank-review-row-${t.transaction_id}`}>
      <div className="flex-1 min-w-[160px]">
        <p className="text-white text-sm">{t.merchant_name || t.name}</p>
        <p className="text-[11px] text-gray-500">{t.date}{sug.kind && <span className="ml-2 text-violet-300"><Sparkles className="inline w-3 h-3" /> JARVIS guess: {sug.kind} / {sug.category}</span>}</p>
      </div>
      <span className={`text-sm font-semibold ${t.amount > 0 ? "text-rose-300" : "text-emerald-300"}`}>{t.amount > 0 ? "-" : "+"}{fmtMoney(Math.abs(t.amount))}</span>
      <div className="flex gap-1">
        {Object.keys(cats).map((k) => (
          <button key={k} onClick={() => { setKind(k); setCat(""); }} data-testid={`bank-kind-${k}-${t.transaction_id}`}
            className={`h-8 px-3 rounded-full text-xs border capitalize transition-colors ${kind === k ? "border-cyan-400 bg-cyan-400/15 text-white" : "border-white/10 text-gray-400 hover:text-white"}`}>{k}</button>
        ))}
      </div>
      <select value={cat} onChange={(e) => setCat(e.target.value)} data-testid={`bank-cat-${t.transaction_id}`}
        className="h-8 rounded-lg bg-[#070A0F] border border-cyan-400/15 text-xs text-white px-2">
        <option value="">Category…</option>
        {(cats[kind] || []).map((c) => <option key={c} value={c}>{c}</option>)}
      </select>
      <label className="text-[11px] text-gray-400 flex items-center gap-1">
        <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} data-testid={`bank-remember-${t.transaction_id}`} /> always for this merchant
      </label>
      <button onClick={save} disabled={busy} className={`${HUD.btn} h-8 w-8 flex items-center justify-center`} data-testid={`bank-save-${t.transaction_id}`}>
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
      </button>
    </div>
  );
};

const shift = (m, d) => { const [y, mo] = m.split("-").map(Number); const x = new Date(y, mo - 1 + d, 1); return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, "0")}`; };

const MonthStepper = ({ month, setMonth }) => (
  <div className="flex items-center h-10 rounded-full border border-cyan-400/15 bg-[#070A0F]" data-testid="bank-month">
    <button onClick={() => setMonth(shift(month, -1))} className="h-10 w-9 flex items-center justify-center text-gray-400 hover:text-cyan-300" data-testid="bank-month-prev"><ChevronLeft className="w-4 h-4" /></button>
    <span className="text-sm text-white w-28 text-center" data-testid="bank-month-label">{new Date(`${month}-01T12:00`).toLocaleDateString(undefined, { month: "long", year: "numeric" })}</span>
    <button onClick={() => setMonth(shift(month, 1))} className="h-10 w-9 flex items-center justify-center text-gray-400 hover:text-cyan-300" data-testid="bank-month-next"><ChevronRight className="w-4 h-4" /></button>
  </div>
);

const OAuthNote = ({ uri }) => (
  <div className={`${HUD.panel} p-4 text-xs text-amber-200 space-y-2 border-amber-400/20`} data-testid="bank-oauth-note">
    <p>Chase, Bank of America, Wells Fargo and other big banks sign in through their own secure page. To enable it, paste this link in your Plaid dashboard: <b>Developers → API → Allowed redirect URIs</b>, then click Connect again.</p>
    <div className="flex gap-2 items-center">
      <code className={`flex-1 rounded-lg px-3 py-2 text-cyan-200 bg-black/50 break-all ${HUD.mono}`} data-testid="bank-redirect-uri">{uri}</code>
      <button onClick={() => { navigator.clipboard?.writeText(uri); toast.success("Copied"); }} className={`${HUD.ghost} w-9 h-9 flex items-center justify-center`} data-testid="bank-redirect-copy"><Copy className="w-4 h-4" /></button>
    </div>
  </div>
);

const download = (year, format) =>
  api.get("/admin/bank/export", { params: { year, format }, responseType: "blob" }).then((r) => {
    const a = document.createElement("a");
    a.href = URL.createObjectURL(r.data);
    a.download = `reflections-${format === "csv" ? "transactions" : "income-expenses"}-${year}.${format}`;
    a.click();
    URL.revokeObjectURL(a.href);
  }).catch(() => toast.error("Export failed"));

const TaxExport = () => {
  const now = new Date().getFullYear();
  const [year, setYear] = useState(now);
  return (
    <div className={`${HUD.panel} p-4 flex flex-wrap items-center gap-3`} data-testid="bank-tax-export">
      <div className="flex-1 min-w-[200px]">
        <p className={HUD.label}>Tax-ready export</p>
        <p className="text-xs text-gray-400 mt-1">Branded PDF summary + full CSV of every filed transaction for your accountant.</p>
      </div>
      <select value={year} onChange={(e) => setYear(Number(e.target.value))} data-testid="bank-export-year" className="h-10 rounded-full bg-[#070A0F] border border-cyan-400/15 text-white text-sm px-3">
        {[now, now - 1, now - 2].map((y) => <option key={y} value={y}>{y}</option>)}
      </select>
      <button onClick={() => download(year, "pdf")} className={`${HUD.btn} h-10 px-4 text-sm flex items-center gap-2`} data-testid="bank-export-pdf"><Download className="w-4 h-4" /> PDF</button>
      <button onClick={() => download(year, "csv")} className={`${HUD.ghost} h-10 px-4 text-sm flex items-center gap-2`} data-testid="bank-export-csv"><Download className="w-4 h-4" /> CSV</button>
    </div>
  );
};

const Chart = ({ data }) => (
  <div className={`${HUD.panel} p-4 h-64`} data-testid="bank-daily-chart">
    <p className={HUD.label}>Daily income vs expenses</p>
    <ResponsiveContainer width="100%" height="90%">
      <BarChart data={data}>
        <XAxis dataKey="date" tickFormatter={(d) => d.slice(8)} stroke="#4b5563" fontSize={10} />
        <Tooltip contentStyle={{ background: "#0B1017", border: "1px solid rgba(34,229,255,.2)" }} formatter={(v) => fmtMoney(v)} />
        <Bar dataKey="income" fill="#34d399" radius={[3, 3, 0, 0]} />
        <Bar dataKey="expense" fill="#fb7185" radius={[3, 3, 0, 0]} />
      </BarChart>
    </ResponsiveContainer>
  </div>
);

export const JarvisBank = () => {
  const [st, setSt] = useState(null);
  const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [sum, setSum] = useState(null);
  const [review, setReview] = useState([]);
  const [txns, setTxns] = useState([]);
  const [syncing, setSyncing] = useState(false);
  const [oauth, setOauth] = useState(null);
  const load = useCallback(() => {
    apiGet("/admin/bank/status").then(setSt).catch(err);
    apiGet("/admin/bank/summary", { month }).then(setSum).catch(() => {});
    apiGet("/admin/bank/transactions", { review: true }).then((d) => setReview(d.items)).catch(() => {});
    apiGet("/admin/bank/transactions", { month }).then((d) => setTxns(d.items)).catch(() => {});
  }, [month]);
  useEffect(load, [load]);
  const sync = () => { setSyncing(true); apiPost("/admin/bank/sync").then(() => { toast.success("Bank synced"); load(); }).catch(err).finally(() => setSyncing(false)); };
  const disconnect = (id) => window.confirm("Disconnect this bank and delete its imported transactions?") && apiDelete(`/admin/bank/items/${id}`).then(load).catch(err);

  if (!st) return <Loader2 className="w-5 h-5 animate-spin text-cyan-300" />;
  const connected = st.items.length > 0;
  return (
    <div className="space-y-4" data-testid="jarvis-bank">
      <div className={`${HUD.panel} p-4 flex flex-wrap items-center gap-3 justify-between`}>
        <div>
          <p className={HUD.label} data-testid="bank-env">Business bank · Plaid {st.env}</p>
          {!st.configured && <p className="text-xs text-amber-300 mt-1" data-testid="bank-not-configured">Plaid isn't set up. Add your keys in JARVIS → Settings → Bank (Plaid).</p>}
          {st.items.some((i) => i.env_mismatch) && <p className="text-xs text-amber-300 mt-1" data-testid="bank-env-mismatch">Some connections were made in a different Plaid mode and won't sync. Remove them and connect again.</p>}
          {connected ? st.items.map((it) => (
            <div key={it.item_id} className="flex items-center gap-2 text-sm text-white mt-1" data-testid={`bank-item-${it.item_id}`}>
              <Landmark className="w-4 h-4 text-cyan-300" /> {it.institution_name}
              {it.env_mismatch && <span className="text-[10px] px-2 rounded-full border border-amber-400/40 text-amber-300" data-testid={`bank-item-mismatch-${it.item_id}`}>{it.env || "sandbox"}, reconnect</span>}
              <span className="text-gray-500 text-xs">{it.accounts?.map((a) => `${a.name} (${a.mask})`).join(", ")}</span>
              <button onClick={() => disconnect(it.item_id)} className="text-gray-500 hover:text-rose-300" data-testid={`bank-disconnect-${it.item_id}`}><Trash2 className="w-4 h-4" /></button>
            </div>
          )) : <p className="text-sm text-gray-400 mt-1">Connect your business account and JARVIS will sort every transaction into income or expenses, asking you about anything it doesn't recognize.</p>}
        </div>
        <div className="flex gap-2 items-center">
          {connected && <MonthStepper month={month} setMonth={setMonth} />}
          {connected && <button onClick={sync} className={`${HUD.ghost} h-10 px-4 text-sm flex items-center gap-2`} data-testid="bank-sync-btn"><RefreshCw className={`w-4 h-4 ${syncing ? "animate-spin" : ""}`} /> Sync</button>}
          <ConnectButton onDone={load} onToken={setOauth} />
        </div>
      </div>
      {oauth && !oauth.oauth_ready && <OAuthNote uri={oauth.redirect_uri} />}
      {connected && <TaxExport />}
      {connected && sum && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <Kpi label="Income" value={fmtMoney(sum.income)} cls="text-emerald-300" testid="bank-income" />
            <Kpi label="Expenses" value={fmtMoney(sum.expense)} cls="text-rose-300" testid="bank-expense" />
            <Kpi label="Net" value={fmtMoney(sum.net)} cls={sum.net >= 0 ? "text-cyan-300" : "text-amber-300"} testid="bank-net" />
            <Kpi label="Needs your call (this month)" value={sum.needs_review_month} cls="text-violet-300" testid="bank-needs-review" />
          </div>
          {sum.needs_review_month > 0 && <p className="text-xs text-gray-400" data-testid="bank-pending-note">Totals count only filed transactions. Still to file this month: {fmtMoney(sum.pending_in)} in, {fmtMoney(sum.pending_out)} out.</p>}
          <div className="grid lg:grid-cols-[1fr_320px] gap-3">
            <Chart data={sum.daily} />
            <div className={`${HUD.panel} p-4 h-64 overflow-y-auto`} data-testid="bank-categories">
              <p className={HUD.label}>Expenses by category</p>
              {sum.by_category.map((c) => (
                <div key={c.category} className="flex justify-between text-sm py-1.5 border-b border-white/5"><span className="text-gray-300">{c.category}</span><span className="text-rose-300">{fmtMoney(c.amount)}</span></div>
              ))}
            </div>
          </div>
          {review.length > 0 && (
            <div className={`${HUD.panel} divide-y divide-cyan-400/5`} data-testid="bank-reviewlist">
              <p className={`${HUD.label} p-3`}>JARVIS needs you to categorize these ({review.length} across all months)</p>
              {review.slice(0, 25).map((t) => <ReviewRow key={t.transaction_id} t={t} cats={st.categories} onDone={load} />)}
            </div>
          )}
          <div className={`${HUD.panel} divide-y divide-cyan-400/5`} data-testid="bank-transactions">
            <p className={`${HUD.label} p-3`}>Transactions · {month}</p>
            {txns.map((t) => (
              <div key={t.transaction_id} className="p-3 flex items-center gap-3 text-sm">
                <span className="text-gray-500 w-24">{t.date}</span>
                <span className="flex-1 text-white truncate">{t.merchant_name || t.name}</span>
                <span className={`text-xs capitalize ${KIND_STYLE[t.kind]}`}>{t.needs_review ? "unreviewed" : `${t.kind} · ${t.category}`}</span>
                <span className={t.amount > 0 ? "text-rose-300" : "text-emerald-300"}>{t.amount > 0 ? "-" : "+"}{fmtMoney(Math.abs(t.amount))}</span>
              </div>
            ))}
            {txns.length === 0 && <p className="p-3 text-sm text-gray-500">No transactions this month.</p>}
          </div>
        </>
      )}
    </div>
  );
};
